/**
 * sl-n8i1.5: Gated Automation Panel
 * sl-y2po.12: Plan-Based Feature Gate Integration
 * sl-za0v: Simplified — SWR needs no provisioning, only plan gate remains
 *
 * Wrapper that gates AutomationConfigPanel on plan level:
 * - workflow-auto-provision must be in workspace plan (Pro+)
 */

import { RevealButton } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { PlayIcon } from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';

import { WorkspaceDialogService } from '../../dialogs';
import {
  FEATURE_KEY_WORKFLOW_AUTO_PROVISION,
  useFeatureGate,
} from '../hooks/use-feature-gate';
import { AutomationConfigPanel } from './automation-panel';
import * as styles from './automation-panel-gated.css';

/**
 * Gated version of AutomationConfigPanel.
 * Renders: plan-blocked or full panel.
 */
export function AutomationConfigPanelGated() {
  const t = useI18n();
  const workspaceDialogService = useService(WorkspaceDialogService);
  const {
    isFeatureAllowed,
    getRequiredPlan,
    isLoading: planLoading,
  } = useFeatureGate();

  const logo = <PlayIcon width={36} height={36} className={styles.icon} />;

  // Plan gate — check workflow-auto-provision (Pro+)
  if (!planLoading && !isFeatureAllowed(FEATURE_KEY_WORKFLOW_AUTO_PROVISION)) {
    const requiredPlan =
      getRequiredPlan(FEATURE_KEY_WORKFLOW_AUTO_PROVISION) ?? 'Pro';
    return (
      <div className={styles.placeholder}>
        {logo}
        <p className={styles.title}>
          {t['com.streamlain.integration.workflow.upgrade.title']()}
        </p>
        <p className={styles.description}>
          {t['com.streamlain.integration.workflow.upgrade.desc']({
            plan: requiredPlan,
          })}
        </p>
        <RevealButton
          label={t['com.streamlain.ai-gate.view-plans']()}
          onClick={() => {
            workspaceDialogService.open('setting', {
              activeTab: 'workspace:plans',
            });
          }}
          wrapperStyle={{ width: 180, borderRadius: 8, overflow: 'hidden' }}
          data-testid="automation-gate-view-plans"
        />
      </div>
    );
  }

  // Plan allowed — render full panel (SWR needs no provisioning)
  return <AutomationConfigPanel />;
}
